"use client";

import Link from "next/link";
import { useState, useEffect, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { Mail, MessageSquare, User, Send, CheckCircle, ArrowLeft, Star, Diamond as DiamondIcon } from "lucide-react";
import PublicFooter from "@/components/layout/PublicFooter";

const planDetails: Record<string, { name: string; label: string; icon: typeof Star; iconColor: string; bgColor: string; borderColor: string }> = {
  standard: {
    name: "Standard",
    label: "You're enquiring about the Standard plan",
    icon: Star,
    iconColor: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
  },
  diamond: {
    name: "Diamond",
    label: "You're enquiring about the Diamond plan",
    icon: DiamondIcon,
    iconColor: "text-violet-600",
    bgColor: "bg-violet-50",
    borderColor: "border-violet-200",
  },
};

function ContactForm() {
  const searchParams = useSearchParams();
  const plan = searchParams.get("plan");
  const planInfo = plan ? planDetails[plan] : null;

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  useEffect(() => {
    if (planInfo) {
      setForm((prev) => ({
        ...prev,
        subject: "sales",
        message: prev.message || `Hi, I'm interested in the ${planInfo.name} plan. I'd like to learn more about pricing and features for my organisation.`,
      }));
    }
  }, [planInfo]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    await new Promise((r) => setTimeout(r, 1200));
    setStatus("success");
  };

  return (
    <main className="min-h-screen" style={{ backgroundColor: "var(--color-bg-page)", color: "var(--color-text-primary)" }}>
      {/* Navigation */}
      <nav
        className="sticky top-0 z-50 flex items-center justify-between px-8 py-4 border-b backdrop-blur-md"
        style={{ backgroundColor: "var(--color-bg-header)", borderColor: "var(--color-border)" }}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center font-bold text-sm text-white">
            M
          </div>
          <Link href="/" className="text-xl font-bold tracking-tight hover:text-blue-600 transition-colors" style={{ color: "var(--color-text-primary)" }}>
            Meridian KPI Platform
          </Link>
        </div>
        <div className="hidden md:flex items-center gap-6 text-sm" style={{ color: "var(--color-text-muted)" }}>
          <Link href="/#features" className="hover:text-blue-600 transition-colors">Features</Link>
          <Link href="/#stats"   className="hover:text-blue-600 transition-colors">Why Us</Link>
          <Link href="/pricing"  className="hover:text-blue-600 transition-colors">Pricing</Link>
          <Link href="/faq"      className="hover:text-blue-600 transition-colors">FAQ</Link>
          <Link href="/contact"  className="text-blue-600 font-medium">Contact</Link>
        </div>
        <div className="flex items-center gap-3">
          <Link
            href="/login"
            className="text-sm px-3 py-2 rounded-lg transition-colors hover:bg-blue-100"
            style={{ color: "var(--color-text-secondary)" }}
          >
            Sign In
          </Link>
          <Link
            href="/signup"
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium"
          >
            Get Started
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-16">
        {/* Back link */}
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-sm hover:text-blue-600 transition-colors mb-10"
          style={{ color: "var(--color-text-muted)" }}
        >
          <ArrowLeft size={15} />
          Back to Home
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: info */}
          <div>
            <span
              className="inline-block text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-5 border"
              style={{ color: "var(--color-accent)", backgroundColor: "var(--color-accent-light)", borderColor: "var(--color-border)" }}
            >
              Get in Touch
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold mb-5 leading-tight" style={{ color: "var(--color-text-primary)" }}>
              We&apos;d love to{" "}
              <span className="text-blue-600">
                hear from you
              </span>
            </h1>
            <p className="text-lg leading-relaxed mb-10" style={{ color: "var(--color-text-muted)" }}>
              Have a question about Meridian KPI Platform? Need help getting started?
              Want to discuss enterprise pricing? Our team is ready to help.
            </p>

            <div className="space-y-5">
              <div className="card p-4 flex items-start gap-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: "var(--color-accent-light)", border: "1px solid var(--color-border)" }}
                >
                  <Mail size={18} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-sm mb-0.5" style={{ color: "var(--color-text-primary)" }}>Email Us</p>
                  <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>Please use the contact form for support enquiries.</p>
                </div>
              </div>

              <div className="card p-4 flex items-start gap-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: "var(--color-accent-light)", border: "1px solid var(--color-border)" }}
                >
                  <MessageSquare size={18} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-sm mb-0.5" style={{ color: "var(--color-text-primary)" }}>Live Chat</p>
                  <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>Available Monday – Friday, 9am – 6pm AEST</p>
                </div>
              </div>

              <div className="card p-4 flex items-start gap-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: "var(--color-accent-light)", border: "1px solid var(--color-border)" }}
                >
                  <User size={18} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-sm mb-0.5" style={{ color: "var(--color-text-primary)" }}>Sales Enquiries</p>
                  <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>Please use the contact form for sales enquiries.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="card p-8">
            {/* Plan acknowledgment banner */}
            {planInfo && status !== "success" && (() => {
              const PlanIcon = planInfo.icon;
              return (
                <div className={`flex items-center gap-3 ${planInfo.bgColor} border ${planInfo.borderColor} rounded-xl p-4 mb-6`}>
                  <div className={`w-9 h-9 rounded-lg ${planInfo.bgColor} border ${planInfo.borderColor} flex items-center justify-center shrink-0`}>
                    <PlanIcon size={18} className={planInfo.iconColor} />
                  </div>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: "var(--color-text-primary)" }}>{planInfo.label}</p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>We&apos;ll tailor our response to your needs.</p>
                  </div>
                </div>
              );
            })()}
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center text-center py-10 gap-4">
                <div className="w-16 h-16 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center">
                  <CheckCircle size={32} className="text-emerald-500" />
                </div>
                <h2 className="text-xl font-bold" style={{ color: "var(--color-text-primary)" }}>Message Sent!</h2>
                <p className="text-sm max-w-xs" style={{ color: "var(--color-text-muted)" }}>
                  Thanks for reaching out. We&apos;ll get back to you within one business day.
                </p>
                <button
                  onClick={() => { setStatus("idle"); setForm({ name: "", email: "", subject: "", message: "" }); }}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700 transition-colors underline underline-offset-2"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <h2 className="text-lg font-semibold mb-6" style={{ color: "var(--color-text-primary)" }}>Send us a message</h2>

                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Jane Smith"
                    className="filter-input w-full px-4 py-3 rounded-xl"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    placeholder="Your company email"
                    className="filter-input w-full px-4 py-3 rounded-xl"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={form.subject}
                    onChange={handleChange}
                    className="filter-select w-full px-4 py-3 rounded-xl"
                  >
                    <option value="" disabled>Select a subject…</option>
                    <option value="general">General Enquiry</option>
                    <option value="support">Technical Support</option>
                    <option value="sales">Sales & Pricing</option>
                    <option value="billing">Billing</option>
                    <option value="feature">Feature Request</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us how we can help…"
                    className="filter-input w-full px-4 py-3 rounded-xl resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all shadow-lg shadow-blue-500/20"
                >
                  {status === "submitting" ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Sending…
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send size={15} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <PublicFooter />
    </main>
  );
}

export default function ContactPage() {
  return (
    <Suspense>
      <ContactForm />
    </Suspense>
  );
}
